
import { ChangeDetectorRef, Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Params,
  Router,
  RouterModule
} from '@angular/router';
import { BreadcrumbService, type BreadcrumbData } from '../breadcrumb.service';
import { SvgIconComponent } from '@k12/app-components';
import { SvgIcon } from '@k12/models';
import { Subscription } from 'rxjs';
import { filter, skip } from 'rxjs/operators';

interface Breadcrumb {
  label: string;
  path?: string;
  queryParams?: Params | null;
}

//IMPORTANT!!! Note to developers: When adding new routes, ensure to add a 'breadcrumb' property in the route's data object for proper breadcrumb display.
//Also make sure to have any breadcrumb routes be part of a parent/child route structure, as this component relies on the ActivatedRoute tree to build the breadcrumbs.
//Please refer to app.routes.ts in the admin module for examples of how to set up routes with breadcrumbs. Specifically with the Task/Task Types routes.
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  imports: [SvgIconComponent, RouterModule],
  standalone: true
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
  breadcrumbs: Breadcrumb[] = [];
  readonly chevronRightIcon = SvgIcon.ChevronRight;

  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly breadcrumbService = inject(BreadcrumbService);
  readonly subscription = new Subscription();
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.breadcrumbs = this.buildBreadcrumbs(this.route.root);
        })
    );

    this.subscription.add(
      // Skip the current value emitted on subscribe; ngOnInit builds that itself.
      // Internal Subject; cannot error by construction, so no error handler is needed.
      this.breadcrumbService.dynamicBreadcrumbs$.pipe(skip(1)).subscribe(() => {
        this.breadcrumbs = this.buildBreadcrumbs(this.route.root);
        this.cdr.detectChanges();
      })
    );

    this.breadcrumbs = this.buildBreadcrumbs(this.route.root);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private buildBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return this.handleTerminalRoute(route, url, breadcrumbs);
    }

    for (const child of children) {
      if (!child.snapshot) {
        continue;
      }

      const routeURL: string = child.snapshot.url ? child.snapshot.url.map(segment => segment.path).join('/') : '';
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const label = this.resolveBreadcrumbLabel(routeURL, child.snapshot, url);

      if (this.shouldAddBreadcrumb(label, routeURL)) {
        breadcrumbs.push({
          label,
          path: url,
          queryParams: child.snapshot.data['breadcrumbQueryParams'] ? { ...child.snapshot.queryParams } : undefined
        });
      }

      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  private handleTerminalRoute(route: ActivatedRoute, url: string, breadcrumbs: Breadcrumb[]): Breadcrumb[] {
    const allBreadcrumbs = this.breadcrumbService.getCurrentBreadcrumbs;
    const currentUrl = this.router.url;

    // Check if we should use dynamic breadcrumbs entirely -- //TODO - Will be removed once the household page is built
    if (allBreadcrumbs.length > 0 && currentUrl.includes('/student/')) {
      return allBreadcrumbs.map(bc => ({
        label: bc.label,
        path: bc.route.startsWith('#') ? undefined : bc.route,
        queryParams: bc.queryParams
      }));
    }

    const prependedBreadcrumbs: Breadcrumb[] = [];

    if (currentUrl.startsWith('/messages') || currentUrl.startsWith('/communications-center')) {
      this.buildPrependedBreadcrumbs(allBreadcrumbs, breadcrumbs, prependedBreadcrumbs);
    }

    if (currentUrl.startsWith('/messages')) {
      this.buildMessagesBreadcrumb(allBreadcrumbs, breadcrumbs, currentUrl, route, url);
    }

    return [...prependedBreadcrumbs, ...breadcrumbs];
  }

  private buildPrependedBreadcrumbs(
    allBreadcrumbs: BreadcrumbData[],
    breadcrumbs: Breadcrumb[],
    prependedBreadcrumbs: Breadcrumb[]
  ): void {
    const sortedBreadcrumbs = [...allBreadcrumbs].sort((a, b) => a.route.length - b.route.length);

    for (const bc of sortedBreadcrumbs) {
      if (breadcrumbs.some(b => b.path === bc.route)) {
        continue;
      }

      if (bc.route && bc.label && bc.route.startsWith('/work-queue')) {
        prependedBreadcrumbs.push({
          label: bc.label,
          path: bc.route,
          queryParams: bc.queryParams
        });
      }
    }
  }

  private buildMessagesBreadcrumb(
    allBreadcrumbs: BreadcrumbData[],
    breadcrumbs: Breadcrumb[],
    currentUrl: string,
    route: ActivatedRoute,
    url: string
  ): void {
    const messagesBreadcrumb = allBreadcrumbs.find(bc => bc.route === '/messages' || bc.route === currentUrl);
    const hasMessagesBreadcrumb = breadcrumbs.some(b => {
      const bPath = b.path || '';
      return bPath === '/messages' || bPath === currentUrl || bPath.includes('messages');
    });

    if (!hasMessagesBreadcrumb) {
      if (messagesBreadcrumb) {
        breadcrumbs.push({
          label: messagesBreadcrumb.label,
          path: currentUrl
        });
      } else {
        const routeData = route.snapshot.data;
        const label = routeData?.['breadcrumb'] || (url && url.includes('messages') ? this.formatLabel('messages') : 'Messages');
        breadcrumbs.push({
          label: label,
          path: url || currentUrl
        });
      }
    }
  }

  private resolveBreadcrumbLabel(routeURL: string, routeSnapshot: ActivatedRouteSnapshot, url: string): string {
    let label = routeSnapshot.data['breadcrumb'] || this.formatLabel(routeURL);

    const dynamicBreadcrumb = this.breadcrumbService.getDynamicBreadcrumb(url);
    if (dynamicBreadcrumb) {
      label = dynamicBreadcrumb;
    }

    return label;
  }

  private shouldAddBreadcrumb(label: string, routeURL: string): boolean {
    return !!(label && routeURL !== '');
  }

  private formatLabel(str: string): string {
    if (!str) return '';
    // Convert camelCase or dash/underscore to Title Case
    return str
      .replace(/[-_]/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  updateBreadcrumbData(route: ActivatedRoute, newBreadcrumb: string) {
    if (route.snapshot.data) {
      route.snapshot.data['breadcrumb'] = newBreadcrumb;
      this.breadcrumbs = this.buildBreadcrumbs(this.route.root);
    }
  }

  rebuildBreadcrumbs() {
    this.breadcrumbs = this.buildBreadcrumbs(this.route.root);
  }
}
